import React from 'react';
import { Button } from 'rebass';
import { FiX } from 'react-icons/fi';
import helpers from 'utils/helpers';

/**
 * Floating bar reporting how many panelists are currently selected, with a way
 * out of the selection (PFOX-1315).
 *
 * Renders nothing while the selection is empty, so it only ever appears as a
 * consequence of the user selecting something.
 *
 * Layering: the bar lives in the page tree (not a portal) so the filters side
 * overlay and modals keep covering it. Its `z-100` sits above the table and
 * below the side overlay's `z-999`. Toasts render bottom-right, so
 * bottom-center stays clear of them.
 *
 * @param {Object} props
 * @param {number} props.count How many panelists are selected. 0 renders nothing.
 * @param {() => void} props.onDeselectAll Clears the selection.
 * @param {number} [props.bottomOffset] Distance from the viewport bottom in px — raise it
 *   on pages with a fixed footer (e.g. the Panelists page's pagination) so the bar sits above it.
 */
export function SelectionCountBar({ count, onDeselectAll, bottomOffset }) {
    if (!count || count < 1) return null;

    return (
        /* Full-width strip so the bar centers itself without a transform, the
           same way ActionPopup does it. It must not swallow clicks on the
           table underneath, hence pointer-events-none here / auto on the bar. */
        <div
            className="fixed bottom-6 left-0 right-0 z-100 text-center pointer-events-none"
            style={bottomOffset !== undefined ? { bottom: `${bottomOffset}px` } : undefined}
        >
            <div
                role="status"
                aria-live="polite"
                data-testid="selection-count-bar"
                className="inline-flex items-center gap-4 py-3 px-4 bg-white border border-light rounded-8 shadow-tooltip pointer-events-auto animate-slide-up-fade-in"
            >
                <span className="text-primary font-medium whitespace-nowrap">
                    {helpers.numberFormat(count)} {count === 1 ? 'panelist selected' : 'panelists selected'}
                </span>

                <Button
                    type="button"
                    variant="secondary-gray"
                    mr={0}
                    onClick={onDeselectAll}
                    data-testid="selection-count-bar-deselect"
                >
                    <FiX /> Deselect All
                </Button>
            </div>
        </div>
    );
}

export default SelectionCountBar;
